// @ts-check

// grand unified formatting function

/**
 *
 * @param {number} val
 * @param {number} digits
 * @returns {string}
 */
function toFixed(val, digits) {
    const string = Number(val).toFixed(digits)

    // Ditch -0.0
    return 1 / Number(string) === -Infinity ? (0).toFixed(digits) : string
}

// Realistically, we have too many changes of spellings and things to not do this, sadly
/**
 *
 * @param {string} type
 * @returns {string}
 */
function getStandardizedType(type) {
    // { alt: standard }
    const altSpellings = {
        abs: 'ab',
        avg_swing_length: 'swing_length_qualified',
        avg_swing_speed: 'swing_speed_qualified',
        barrels: 'barrel',
        barrels_per_bbe_percent: 'barrel_batted_rate',
        barrels_per_pa_percent: 'barrels_per_pa',
        batting_avg: 'ba',
        bb: 'walk',
        dbls: 'double',
        ev: 'launch_speed',
        hardhit_percent: 'hard_hit_percent',
        hits: 'hit',
        hrs: 'home_run',
        isolated_power: 'iso',
        on_base_percent: 'obp',
        on_base_plus_slg: 'ops',
        pitch_speed: 'pitch_velocity',
        singles: 'single',
        slg_percent: 'slg',
        so: 'strikeout',
        triples: 'triple',
        attack_angle: 'bat_path_angle',
        attack_angle_asc: 'bat_path_angle',
        attack_direction: 'bat_path_angle',
        attack_direction_pullopp: 'bat_path_angle',
        swing_path_tilt: 'bat_path_angle',
        avg_plane_vertical_angle: 'bat_path_angle',
        avg_plane_vertical_angle_asc: 'bat_path_angle',
        vertical_swing_path: 'bat_path_angle',
        bat_minus_plate_pos_y_inches: 'dist_in_inches',
        intercept_ball_minus_plate_pos_y_inches: 'dist_in_inches',
        intercept_ball_minus_batter_pos_y_inches: 'dist_in_inches',
        bat_minus_plate_pos_x_inches: 'dist_in_inches',
        intercept_ball_minus_plate_pos_x_inches: 'dist_in_inches',
        intercept_ball_minus_batter_pos_x_inches: 'dist_in_inches',
        pull_percent_airballs: 'airballs_percent',

        // bit of a hack
        spin_rate: '_avg_spin',
    }

    return altSpellings[type] || type
}

/**
 *
 * @param {number|string} val
 * @returns {string}
 */
function threeDigitFormat(val) {
    const num = Number(val)

    if (num >= 0 && num < 1) {
        return num.toFixed(3).slice(-4)
    }

    if (num < 0 && num > -1) {
        return `-${Math.abs(num).toFixed(3).slice(-4)}`
    }

    return num.toFixed(3)
}

const threeDigitTypes = [
    'ba',
    'xba',
    'est_ba',
    'obp',
    'xobp',
    'est_obp',
    'slg',
    'xslg',
    'est_slg',
    'iso',
    'xiso',
    'est_iso',
    'ops',
    'babip',
    'est_babip',
    'woba',
    'xwoba',
    'est_woba',
    'wobacon',
    'xwobacon',
    'est_wobacon',
    'bacon',
    'xbacon',
    'est_bacon',
]

const thousandthTypes = [
    'delta_pitcher_run_exp',
    'delta_run_exp',
    'unadj_delta_pitcher_run_exp',
    'unadj_delta_run_exp',
    'delev_delta_pitcher_run_exp',
    'delev_delta_run_exp',
]

const hundredthTypes = [
    'catcher_exchange',
    'catcher_poptime',
    'pop_2b_sba',
    'home_to_first',
    'running_splits',
    'era',
    'xera',
    'exchange_time',
    'pop_time',
    'rate_cs_aa',
    'runner_stealing_runs_drawer',
    'avg_hr_trot',
    'home_runs_per_nine',
]

const tenthTypes = [
    'hard_hit_percent',
    'launch_speed',
    'exit_velocity_avg',
    'avg_best_speed',
    'hyper_speed',
    'avg_hyper_speed',
    'pitch_tempo_empty',
    'sweet_spot_percent',
    'barrel_batted_rate',
    'barrels_per_pa',
    'launch_angle_avg',
    // Pitch metrics
    'fastball_velo',
    'breaking_velo',
    'offspeed_velo',
    'ff_avg_speed',
    'fastball_extension',
    'pitch_type_percent',
    'pitch_velocity',
    'api_break_z_with_gravity',
    'api_break_z_induced',
    'api_break_x_arm',
    'api_break_x_batter_in',
    'active_spin_percent',
    // Bat tracking
    'sweetspot_speed_mph',
    'swing_speed',
    'swing_speed_qualified',
    'swing_length',
    'swing_length_qualified',
    'bat_speed_avg',
    'metric-percent',
    'ideal_angle_rate',
    'miss_distance',
    'swing_timing_dist',

    // bat tracking from sortable stats
    'squared_up_contact',
    'squared_up_swing',
    'blasts_swing',
    'blasts_contact',
    'fast_swing_rate',
    'accel_time_ms',

    // Batted Ball
    'popups_percent',
    'flyballs_percent',
    'linedrives_percent',
    'groundballs_percent',
    'airballs_percent',
    'pull_percent',
    'straightaway_percent',
    'opposite_percent',
    'poorlyweak_percent',
    'poorlytopped_percent',
    'poorlyunder_percent',
    'flareburner_percent',
    'solidcontact_percent',
    'hr_flyballs_percent',
    'dist_in_inches',
    // Plate disc
    'in_zone_percent',
    'out_zone_percent',
    'edge_percent',
    'z_swing_percent',
    'oz_swing_percent',
    'iz_contact_percent',
    'oz_contact_percent',
    'whiff_percent',
    'f_strike_percent',
    'f_swing_percent',
    'swing_percent',
    'meatball_swing_percent',
    'meatball_percent',
    'z_swing_miss_percent',
    'oz_swing_miss_percent',
    'swing_miss_percent',
    // Basics
    'k_percent',
    'bb_percent',
    'putaway_percent',
    // catcher arm
    'catcher_arm',
    // Fielding
    'arm_strength_inf',
    'arm_strength_of',
    'arm_overall',
    'outfield_jump',
    'jump_out_percent',
    // Basestealing
    'r_sec_minus_prim_lead',
    'r_sec_minus_prim_lead_sbx',
    // Baserunning
    'sprint_speed',
    'jump',
    'runs_stolen_on_running_act_drawer',
    // Pitcher Stealing
    'r_primary_lead',
    'r_secondary_lead',
    'r_sec_minus_prim_lead',
    'r_primary_lead_sbx',
    'r_secondary_lead_sbx',
    'r_sec_minus_prim_lead_sbx',
    // Catcher framing
    'strike_rate',
    // Catcher throwing
    'arm_strength',
    'distance_to_target',
    'seasonal_sprint_speed',
    // Other
    'game_score',
    'batter_run_value_per_100',
    'pitcher_run_value_per_100',
    'arm_angle',
    'strike_zone_distance',
    'run_value_zone',
    'exp_abs_chals',
    'batter_run_exp_contact',
    'batter_run_exp_take',
    'batter_run_exp_swing',
    'batter_run_exp',
    'pitcher_run_exp_contact',
    'pitcher_run_exp_take',
    'pitcher_run_exp_swing',
    'pitcher_run_exp',
    'edge_dist',
]

// Should these be noops? Eh.
const wholeTypes = [
    'barrel',
    'popups',
    'flyballs',
    'linedrives',
    'groundballs',
    'launch_angle',
    'bbdist',
    // Plate disc.
    'in_zone',
    'out_zone',
    'edge',
    'in_zone_swing',
    'out_zone_swing',
    'in_zone_swing_miss',
    'out_zone_swing_miss',
    // Basic
    'pitch_count',
    'pa',
    'ab',
    'bip',
    'hit',
    'single',
    'double',
    'triple',
    'home_run',
    'walk',
    'strikeout',
    'hbp',
    'batted_ball',
    'pitch_count_fastball',
    'pitch_count_offspeed',
    'pitch_count_breaking',
    'whiffs',
    'swings',
    // Total baserunning
    'runner_runs_sb',
    'runner_runs_xb',
    // Baserunning
    'baserunning',
    'fielder_runs',
    'runner_runs',
    'runner_runs_swipe',
    'runner_runs_snipe',
    'runner_runs_freeze',
    'fielder_runs_swipe',
    'fielder_runs_snipe',
    'fielder_runs_freeze',
    'bolts',
    // Basestealing
    'runs_stolen_on_running_act',
    'simple_stolen_on_running_act',
    'net_act_plus',
    'net_act_minus',
    'n_init',
    'n_plus',
    'n_minus',
    // Baserunning Top Performers
    'baserunning_runs',
    'basestealing',
    'extra_bases_taken',
    // Pitcher Stealing
    'runs_prevented_on_running_attr',
    'simple_prevented_on_running_attr',
    'net_attr_plus',
    'net_attr_minus',
    // Runner Stealing
    'runs_stolen_on_running_act',
    'simple_stolen_on_running_act',
    'net_act_plus',
    'net_act_minus',
    // Fielding Runs
    'runs_prevented',
    // Run Value
    'run_value',
    'swing_take_run_value',
    'run_exp',
    'pitcher_run_exp',
    'unadj_run_exp',
    'unadj_pitcher_run_exp',
    'delev_run_exp',
    'delev_pitcher_run_exp',
    'runner_run_value',
    'fielding_run_value',
    'fielding_run_value_arm',
    'pitch_run_value_fastball',
    'pitch_run_value_breaking',
    'pitch_run_value_offspeed',
    'pitcher_stealing_runs',
    'runner_stealing_runs',

    //
    'oaa',
    'outs_above_average',
    'cs_above_average',
    'blocks_above_average',
    'catcher_blocking',
    'catcher_throwing',
    'fielding_run_value',
    'fielding_run_value_framing',
    'framing',
    'player_age', // lazy whole ages, someday improve
    'arm_angle_viz', // for some reason generally going with whole angles, except not always

    // Bat tracking
    'swords',
    'num_swings',
    'peak_accel_ms',

    // Catcher throwing
    'n_xcs_with_untracked_over_xcs',
    'n_xcs_with_onfly_other_over_xcs',
    'n_xcs_with_ground_other_over_xcs',
    'n_xcs_with_accuracy_over_xcs',
    'n_xcs_with_exchange_over_xcs',
    'n_xcs_with_flight_over_xcs',
    'n_xcs',
    'n_cs_aa',
    'cs_raa',

    'venue_fence_distance',
    'venue_square_footage',
    'venue_fence_height',

    'pitch_spin_search',

    'abs_challenges',
]

const percentWhole = [
    // Baserunning
    'rate_att_xb',
    'est_rate_att_generic_runner',
    'est_rate_att_generic_fielder',
    'rate_att_xb_diff_runner',
    'rate_att_xb_diff_fielder',
    'base_running_rate_safe',
    'base_running_rate_safe_per_attempt',
    'rate_xcs',
    'rate_cs',
    'knee_percent',
    'cs_percent',
    // bat tracking
    'swing_timing_percent',
    // player page pitch percent
    'handed_pitch_percent',
    // ABS
    'abs_chal_percent',
    // gamefeed pitch percent
    'gamefeed_pitch_percent',
]

const percentTenth = [
    // Pitcher Steal
    'rate_sbx',
    // bat tracking
    'swings_qualified_percent',
    'avg_is_sweetspot_speed_high_qualified', // fast swing rate
    'squared_up_per_bat_contact',
    'squared_up_per_swing',
    'squared_up_with_speed_per_swing',
    'squared_up_with_speed_per_bat_contact',
    // Batted ball
    'batted_ball_rate',
    'rate_ideal_attack_angle',
    // Basestealing
    'rate_sbx',
    // Pitching
    'pitch_percent',
    'whiff_rate_percent',
    // Other
    'called_strike_prob',
    'venue_square_footage_diff',
    'abs_chal_rate_percent',
    'home_run_percent',
    // ABS
    'abs_chal_rate_percent',
]

const angleWhole = ['bat_path_angle', 'launch_angle_search']

const defaultOpts = {
    placeholder: '--',
    isMissingFn: (val) => [null, undefined, ''].includes(val),
    isDiff: false,
    ignoreExtremaHandling: false,
}

/**
 *
 * @param {string} type
 * @returns {boolean}
 */
function isMovementType(type) {
    return (
        type.includes('movement_vertical_') ||
        type.includes('movement_horizontal_')
    )
}

/**
 *
 * @param {string} type
 * @returns {boolean}
 */
function isAvgSpeed(type) {
    return type.includes('_avg_speed')
}

/**
 *
 * @param {string} type
 * @returns {boolean}
 */
function isAvgSpin(type) {
    return type.includes('_avg_spin')
}

/**
 *
 * @param {string} type
 * @returns {boolean}
 */
function isActiveSpin(type) {
    return type.includes('formatted_active_spin_')
}

// grand unified formatting function
/**
 *
 * @param {string|number|null|undefined} val
 * @param {string} rawType
 * @param {{ placeholder?: any, isMissingFn?: function, isDiff?: boolean, removePercent?: boolean }} [opts]
 * @returns {string}
 */
function guff(val, rawType, opts = defaultOpts) {
    const mergedOpts = { ...defaultOpts, ...opts }

    const type = getStandardizedType(rawType)

    try {
        // Add some extra checks to pacify typescript
        if (mergedOpts.isMissingFn(val) || val === undefined || val === null) {
            // How is TS okay with turning any here?
            return mergedOpts.placeholder
        }

        const numberVal = Number(val)

        if (Number.isNaN(numberVal)) {
            return mergedOpts.placeholder
        }

        // Three Digit Formats (with diffs)
        if (threeDigitTypes.includes(type) && mergedOpts.isDiff) {
            if (numberVal > -1 && numberVal < 1) {
                const formattedVal = threeDigitFormat(numberVal)
                if (Number(formattedVal) > 0) {
                    return `+${formattedVal}`
                }

                return formattedVal
            }

            const formattedVal = toFixed(numberVal, 3)
            if (Number(formattedVal) > 0) {
                return `+${formattedVal}`
            }
            if (Number(formattedVal) < 0) {
                return formattedVal
            }

            return formattedVal
        }

        if (threeDigitTypes.includes(type)) {
            return threeDigitFormat(numberVal)
        }

        if (thousandthTypes.includes(type)) {
            const formattedValue = toFixed(numberVal, 3)
            return Number(formattedValue) > 0 && mergedOpts.isDiff
                ? `+${formattedValue}`
                : formattedValue
        }

        if (hundredthTypes.includes(type)) {
            const formattedValue = toFixed(numberVal, 2)
            return Number(formattedValue) > 0 && mergedOpts.isDiff
                ? `+${formattedValue}`
                : formattedValue
        }

        if (
            tenthTypes.includes(type) ||
            isMovementType(type) ||
            isActiveSpin(type) ||
            isAvgSpeed(type)
        ) {
            const formattedValue = toFixed(numberVal, 1)
            return Number(formattedValue) > 0 && mergedOpts.isDiff
                ? `+${formattedValue}`
                : formattedValue
        }

        if (wholeTypes.includes(type) || isAvgSpin(type)) {
            const formattedValue = Math.round(numberVal) || 0
            // FWIW - This avoids the -0 problem because String(-0) => '0' and -0 || 0 => 0
            return Number(formattedValue) > 0 && mergedOpts.isDiff
                ? `+${formattedValue.toLocaleString()}`
                : formattedValue.toLocaleString() // for consistently returning a string
        }

        if (percentWhole.includes(type)) {
            const roundedNumber = Math.round(numberVal * 100)

            let formattedValue = String(roundedNumber)

            if (roundedNumber === 100 && numberVal < 1) {
                formattedValue = '>99'
            } else if (roundedNumber === 0 && numberVal > 0) {
                formattedValue = '<1'
            }

            const suffix = mergedOpts.removePercent ? '' : '%'
            // FWIW - This avoids the -0 problem because String(-0) => '0'
            return roundedNumber > 0 && mergedOpts.isDiff
                ? `+${formattedValue}${suffix}`
                : formattedValue + suffix
        }

        if (percentTenth.includes(type)) {
            let formattedValue = (numberVal * 100).toFixed(1)
            const suffix = mergedOpts.removePercent ? '' : '%'

            if (!mergedOpts.ignoreExtremaHandling) {
                if (formattedValue === '100.0' && numberVal < 1) {
                    formattedValue = '>99.9'
                } else if (formattedValue === '0.0' && numberVal > 0) {
                    formattedValue = '<0.1'
                }
            }

            // FWIW - This avoids the -0 problem because String(-0) => '0'
            return Number(formattedValue) > 0 && mergedOpts.isDiff
                ? `+${formattedValue}${suffix}`
                : formattedValue + suffix
        }

        if (angleWhole.includes(type)) {
            const formattedValue = Math.round(numberVal)
            // FWIW - This avoids the -0 problem because String(-0) => '0'
            return Number(formattedValue) > 0 && mergedOpts.isDiff
                ? `+${formattedValue}°`
                : `${formattedValue}°`
        }

        throw new Error(`type: ${type} not found`)
    } catch (e) {
        console.error('GUFF ERROR', e, val, type)
        return String(val)
    }
}

const validTypes = [
    ...threeDigitTypes,
    ...thousandthTypes,
    ...hundredthTypes,
    ...tenthTypes,
    ...wholeTypes,
    ...percentWhole,
    ...percentTenth,
    ...angleWhole,
]

const validTypeMap = validTypes.reduce((acc, curr) => {
    acc[curr] = true
    return acc
}, {})

/**
 *
 * @param {string} type
 * @returns {boolean}
 */
function isGuffable(type) {
    const standardType = getStandardizedType(type)
    return (
        Boolean(validTypeMap[standardType]) ||
        isAvgSpeed(standardType) ||
        isMovementType(standardType) ||
        isActiveSpin(standardType) ||
        isAvgSpin(standardType)
    )
}

// table
/**
 *
 * @param {string|number|null|undefined} val
 * @param {any} _
 * @param {{ field: string}} column
 * @returns {string}
 */
function displayFn(val, _, { field }) {
    return guff(val, field)
}

// table
/**
 *
 */
/**
 *
 * @param {string} type
 * @returns {(val: string|number|null|undefined) => string}
 */
function displayFnFactory(type) {
    return function displayFunc(val) {
        return guff(val, type)
    }
}

module.exports = {
    displayFn,
    displayFnFactory,
    guff,
    isGuffable,
}
